import costData from '../data/integrated_ai_costs.json';
import { CostData } from '../types';

// API 응답 타입 정의
interface ApiCostItem {
  updated_at: string;
  model: string;
  date: string;
  user_id: string;
  api_key: string;
  sk: string;
  provider: string;
  cost_usd: number;
  pk: string;
}

interface ApiResponse {
  success: boolean;
  data: {
    costData: ApiCostItem[];
    aggregatedData: any[];
    kpis: any;
    exchangeRate: number;
  };
}

// API 응답을 CostData 배열로 변환하는 함수
export const convertApiToCostData = (apiItems: ApiCostItem[]): CostData[] => {
  return apiItems.map(item => ({
    date: item.date,
    time: item.updated_at.split('T')[1]?.split('.')[0] || '00:00:00', // ISO 시간에서 시간 부분 추출
    provider: item.provider,
    identifier: `${item.user_id}#${item.api_key}`, // model 부분 제거, user_id#api_key 형태로 변경
    cost_usd: item.cost_usd
  }));
};

// API에서 비용 데이터를 가져와서 CostData 배열로 반환
export const fetchCostDataFromApi = async (): Promise<CostData[]> => {
  try {
    console.log('🚀 DynamoDB API에서 데이터를 가져오는 중...');
    
    const response = await fetch('https://w531ks20bb.execute-api.ap-northeast-1.amazonaws.com/dev/api/cost-data');
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const apiResponse: ApiResponse = await response.json();
    console.log('전체 api 데이터 :', apiResponse);
    
    if (!apiResponse.success) {
      throw new Error('API 응답이 실패했습니다');
    }
    
    // API 데이터를 CostData 형태로 변환
    const costDataArray = convertApiToCostData(apiResponse.data.costData);
    
    console.log(`✅ ${costDataArray.length}개의 비용 데이터를 성공적으로 가져왔습니다`);
    console.log('📊 샘플 데이터:', costDataArray.slice(0, 3));
    
    return costDataArray;
    
  } catch (error) {
    console.error('❌ API 호출 실패:', error);
    console.log('📁 로컬 JSON 파일로 대체합니다');
    
    // 에러 발생시 로컬 데이터 사용
    return getCostData() as CostData[];
  }
};

// 특정 조건으로 필터링된 데이터 가져오기
export const fetchFilteredCostData = async (filters: {
  provider?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
}): Promise<CostData[]> => {
  try {
    const params = new URLSearchParams();
    
    if (filters.provider) params.append('provider', filters.provider);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.userId) params.append('user_id', filters.userId);
    
    const url = `https://w531ks20bb.execute-api.ap-northeast-1.amazonaws.com/dev/api/cost-data?${params.toString()}`;
    
    console.log('🔍 필터링된 데이터 요청:', url);
    
    const response = await fetch(url);
    const apiResponse: ApiResponse = await response.json();
    
    if (!apiResponse.success) {
      throw new Error('API 응답이 실패했습니다');
    }
    
    const costDataArray = convertApiToCostData(apiResponse.data.costData);
    
    console.log(`✅ 필터링된 ${costDataArray.length}개의 데이터를 가져왔습니다`);
    
    return costDataArray;
    
  } catch (error) {
    console.error('❌ 필터링된 API 호출 실패:', error);
    return [];
  }
};

// 기본 데이터 가져오기 (동기적)
export const getCostData = () => {
  return costData;
};

// API 테스트용 별도 함수
export const testApiData = async () => {
  try {
    console.log('🚀 DynamoDB 테스트 시작...');
    const response = await fetch('https://w531ks20bb.execute-api.ap-northeast-1.amazonaws.com/dev/api/cost-data');
    const data = await response.json();
    
    // 상위 5개만 로그 출력
    console.log('📊 DynamoDB 상위 5개 아이템:');
    data.slice(0, 5).forEach((item: any, i: number) => {
      console.log(`[${i+1}]`, item);
    });
    
    return data;
  } catch (error) {
    console.warn('API 호출 실패:', error);
    return costData;
  }
};

// 호환성을 위해 유지
export const getCostDataSync = () => {
  return costData;
}; 